import * as yup from 'yup';
import {
  Appointments,
  Businesses,
  Customers,
  Discounts,
  Orders,
  PaymentLineItems,
  PaymentMethod,
  PaymentRefundReason,
  Payments,
  PaymentTransfers,
  SplitPaymentsGroups,
  Users,
  ProviderCommissionType,
} from '@prisma/client';
import type Stripe from 'stripe';
import { SORTABLE_KEYS } from '../../constants/payments';
import {
  Appointment,
  Customer,
  CustomerEmergencyContact,
} from '../../interfaces';
import { OrderWithItems } from '../orders';
import { SplitPaymentMethod } from '../../context/SplitPaymentsContext/context';
import {
  formatDollarAmount,
  testPhoneNumbervalue,
} from '../../utils/global/helper';
import type {
  CheckoutFormValues,
  CheckoutPaymentMethod,
} from '../appointments';
import { getEmailSchema, Medium, Mediums } from '../global';

export interface ProviderCommissionOption {
  type: ProviderCommissionType;
  rate: number;
}

export interface PaymentsSplit {
  id: string;
  type: 'tip' | 'payment';
  amount: number;
  transfersAmount: number;
}

export interface PaymentWithTransfers extends Payments {
  transfers?: PaymentTransfers[];
}

export type PaymentLineItemWithService = PaymentLineItems & {
  appointmentService?: {
    service: { serviceName: string } | null;
  } | null;
};

export interface PaymentWithTransfersAndLineItems extends PaymentWithTransfers {
  lineItems: PaymentLineItemWithService[];
}

export interface PaymentWithTransfersOrders
  extends PaymentWithTransfersAndLineItems {
  order:
    | (OrderWithItems & {
        customer:
          | (Customer & {
              user: Users;
              internalNotes?: string;
            })
          | null;
      })
    | null;
}

export interface FullPayment extends PaymentWithTransfersOrders {
  customer:
    | (Customers & {
        user: Users;
        internalNotes?: string;
      })
    | null;
  appointment: Appointment | null;
  splitPaymentsGroup?: {
    payments: (Pick<Payments, 'id' | 'status'> & {
      lineItems: Pick<PaymentLineItems, 'type'>[];
    })[];
  };
}

export interface SplitPaymentsGroupWithCustomer extends SplitPaymentsGroups {
  customer: Customers | null;
}

export interface CreateSplitPaymentResponse extends InitiatePaymentResponse {
  payment: Payments;
  /** Customer-facing coverage of the inflated group total by this payment. */
  customerFacingShareCents?: number;
  /** Fee-bearing base (service + retail) covered by this payment. */
  baseCoveredCents?: number;
}

export interface SplitPaymentsGroupWithPayments extends SplitPaymentsGroups {
  payments: Payments[];
}

export const getRefundPaymentSchema = (max: number) =>
  yup.object({
    amount: yup
      .number()
      .required('Amount is required')
      .min(0.01, 'Amount must be at least 0.01')
      .max(max, `Amount must be less than or equal to ${max}`),
    reason: yup
      .mixed<PaymentRefundReason>()
      .oneOf(Object.values(PaymentRefundReason)),
    otherReason: yup.string().when('reason', {
      is: PaymentRefundReason.OTHER,
      then: value => value.required('Reason is required'),
    }),
  });

export const orderItemSchema = yup.object({
  itemId: yup.string().nullable().optional(),
  price: yup
    .number()
    .min(0)
    .when('itemId', {
      is: Boolean,
      then: schema => schema.nullable().optional(),
      otherwise: schema => schema.required(),
    }),
  name: yup.string().when('itemId', {
    is: Boolean,
    then: schema => schema.nullable().optional(),
    otherwise: schema => schema.required(),
  }),
  quantity: yup.number().min(1).required(),
});

export const orderPaymentSchema = yup.object({
  orderItems: yup.array().of(orderItemSchema).required(),
  orderIdToDelete: yup.string().nullable().optional(),
  employeeId: yup.string().nullable().optional(),
});

export const additionalServiceItemSchema = yup.object({
  serviceId: yup.number().integer().nullable().optional(),
  serviceName: yup.string().required(),
  employeeId: yup.string().required(),
  providerCommissionType: yup
    .mixed<ProviderCommissionType>()
    .oneOf(Object.values(ProviderCommissionType))
    .required(),
  price: yup.number().min(0).required(),
});

export type AdditionalServiceCheckoutItem = yup.InferType<
  typeof additionalServiceItemSchema
> & { id: string };

export const appointmentPaymentSchema = yup.object({
  appointmentId: yup.string().required('Please select an appointment'),
  appointmentPrice: yup.number().min(0).required('Please enter a price'),
  isNoCharge: yup.boolean().default(false).nullable().optional(),
  providerCommissionType: yup
    .mixed<ProviderCommissionType>()
    .oneOf(Object.values(ProviderCommissionType))
    .nullable()
    .optional(),
  employeeId: yup.string().nullable().optional(),
  additionalServices: yup.array().of(additionalServiceItemSchema).optional(),
});

const giftCardTypeSchema = yup
  .mixed<GiftCardType>()
  .oneOf(['internal', 'external'])
  .when('paymentMethod', {
    is: 'GIFT_CARD',
    then: schema => schema.required('Gift card type is required'),
    otherwise: schema => schema.nullable().optional(),
  });

export const initiatePaymentSchema = yup.object({
  paymentMethod: yup
    .mixed<PaymentMethod>()
    .oneOf(Object.values(PaymentMethod))
    .required('Payment method is required'),
  customerId: yup
    .string()
    .nullable()
    .when('appointment', {
      is: (v: unknown) => !!v,
      then: schema => schema.required('Customer is invalid'),
      otherwise: schema => schema.optional(),
    }),
  businessId: yup.string().required('Business is invalid'),
  tipAmount: yup.number().min(0).default(0).nullable().optional(),
  tipPercentage: yup.number().min(0).max(1).nullable().optional(),
  isManualCheckout: yup.boolean().default(false).nullable().optional(),
  isCashTaxed: yup.boolean().default(false).nullable().optional(),
  giftCardAmountCents: yup.number().integer().min(0).nullable().optional(),
  paymentIntentId: yup.string().nullable().optional(),
  order: orderPaymentSchema.nullable().optional(),
  appointment: appointmentPaymentSchema.nullable().optional(),
  discountId: yup.string().nullable().optional(),
  giftCardType: giftCardTypeSchema,
});

export const createSplitPaymentsGroupSchema = yup.object({
  customerId: yup
    .string()
    .nullable()
    .when('appointment', {
      is: (v: unknown) => !!v,
      then: schema => schema.required('Customer is invalid'),
      otherwise: schema => schema.optional(),
    }),
  businessId: yup.string().required('Business is invalid'),
  order: orderPaymentSchema.nullable().optional(),
  appointment: appointmentPaymentSchema.nullable().optional(),
  discountId: yup.string().nullable().optional(),
});

export const createSplitPaymentSchema = yup.object({
  paymentMethod: yup
    .mixed<PaymentMethod>()
    .oneOf(Object.values(PaymentMethod))
    .required('Payment method is required'),
  amountCents: yup.number().integer().min(0).required('Amount is required'),
  isManual: yup.boolean().nullable().optional(),
  giftCardType: giftCardTypeSchema,
});

export type RefundPaymentPayload = yup.InferType<
  ReturnType<typeof getRefundPaymentSchema>
>;

export interface RefundPaymentResponse extends PaymentWithTransfers {}

export const REFUND_REASON_LABELS: Record<PaymentRefundReason, string> = {
  [PaymentRefundReason.DUPLICATE]: 'Duplicate',
  [PaymentRefundReason.REQUESTED_BY_CUSTOMER]: 'Requested by Customer',
  [PaymentRefundReason.OTHER]: 'Other',
};

export type InitiatePaymentPayload = yup.InferType<
  typeof initiatePaymentSchema
>;

export interface InitiatePaymentResponse {
  order: Orders;
  appointment: Appointments;
  paymentIntent:
    | {
        paymentId: string;
        id: string;
        client_secret: string;
      }
    | undefined;
  business: Businesses | null;
  customerId: string | null;
  paymentIds: string[];
}

export type CreateSplitPaymentsGroupPayload = yup.InferType<
  typeof createSplitPaymentsGroupSchema
>;

export type CreateSplitPaymentPayload = yup.InferType<
  typeof createSplitPaymentSchema
>;

export interface GuestReceiptContactValues {
  email: string;
  phone: string;
}

export const getGuestReceiptContactSchema = (mediums: Medium[]) => {
  const emailRequired = mediums.includes(Mediums.EMAIL);
  const phoneRequired = mediums.includes(Mediums.SMS);
  return yup.object({
    email: emailRequired
      ? getEmailSchema('Please enter a valid email').required(
          'Email is required',
        )
      : yup.string().nullable().optional(),
    phone: phoneRequired
      ? yup.string().required('Phone number is required').test({
          test: testPhoneNumbervalue,
          message: 'Invalid phone number',
        })
      : yup.string().nullable().optional(),
  });
};

export interface CheckoutData {
  appointment: {
    originalPriceCents: number;
    discountAmountCents: number;
    salesTaxCents: number;
    isDepositPaid: boolean;
    depositAmountPaidCents: number;
    tipCents: number;
  };
  order: {
    originalPriceCents: number;
    discountAmountCents: number;
    salesTaxCents: number;
    tipCents: number;
  };
  totalBeforeTaxesAndFeesCents: number;
  totalOriginalPriceCents: number;
  discount: Discounts | null;
  discountAmountCents: number;
  feeCents: number;
  discountedPriceCents: number;
  salesTaxCents: number;
  giftCardAmountCents: number;
  totalPriceCents: number;
  totalPriceWithoutGiftCardCents: number;
  priceCents: number;
  isTotalCoveredByGiftCard: boolean;
  hasTaxRate: boolean;
  cashDiscountEnabled: boolean;
  depositCashDiscountCents: number;
  depositBundledFeeCents: number;
  cashDiscountCents: number;
}

export type PaymentsGroup = {
  groupKey: string;
  payments: (Payments & {
    transfers?: PaymentTransfers[];
    lineItems?: PaymentLineItemWithService[];
  })[];
  appointment: Appointments | null;
  order: Orders | null;
  customer:
    | (Customers & {
        user: Users;
        emergencyContacts: CustomerEmergencyContact[];
      })
    | null;
};

/** Returned by the detail endpoint (/payments/:id/group) — always includes lineItems, transfers, and orderItems */
export type PaymentsGroupDetail = Omit<PaymentsGroup, 'payments' | 'order'> & {
  payments: PaymentWithTransfersAndLineItems[];
  order: OrderWithItems | null;
};

export type PaymentsGroupResponse = {
  groups: PaymentsGroup[];
  /** null on pages > 1 — use the page-1 cached value for display */
  totalCount: number | null;
};

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export type AddPaymentMethodPayload = {
  amount: number;
  paymentMethod: PaymentMethod;
  isManual?: boolean;
  giftCardType?: GiftCardType;
};

export type SplitPaymentOnFinishArgs = {
  customerId: string | undefined | null;
  splitPaymentsGroupId?: string;
  paymentIds?: string[];
  hasCashLeg?: boolean;
};

export const getSplitPaymentAmountSchema = (
  totalPriceCents: number,
  paymentMethods: SplitPaymentMethod[],
  currentIndex: number,
) => {
  const sum = paymentMethods
    .slice(0, currentIndex)
    .reduce((acc, curr) => acc + curr.amount, 0);

  const roundedMax = Math.round(totalPriceCents - sum * 100) / 100;
  const max = Math.max(0, roundedMax);
  return yup
    .number()
    .typeError('Enter a valid amount')
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .min(
      0.01,
      `Payment amount must be at least ${formatDollarAmount(0.01, true)}`,
    )
    .max(
      max,
      `Payment amount must be less than or equal to ${formatDollarAmount(
        max,
        true,
      )}`,
    )
    .test('decimal-places', 'Enter a valid amount', val => {
      if (typeof val !== 'number') return false;
      const rounded = Math.round(val * 100) / 100;
      return val === rounded;
    })
    .required('Enter a valid amount');
};

const splitPaymentMethods = [
  'TERMINAL',
  'MANUAL',
  'CASH',
  'DIGITAL',
  'GIFT_CARD',
] as const satisfies readonly CheckoutPaymentMethod[];

export const getSplitPaymentSchema = (
  totalPriceCents: number,
  paymentMethods: SplitPaymentMethod[],
  currentIndex: number,
) => {
  const numberSchema = getSplitPaymentAmountSchema(
    totalPriceCents,
    paymentMethods,
    currentIndex,
  );
  return yup.object({
    amount: numberSchema,
    paymentMethod: yup
      .mixed<(typeof splitPaymentMethods)[number]>()
      .oneOf(splitPaymentMethods)
      .required('Payment method is required'),
    giftCardType: giftCardTypeSchema,
  });
};

export type SplitPaymentPayload = yup.InferType<
  ReturnType<typeof getSplitPaymentSchema>
>;

export type SplitPaymentForm = {
  amount: string;
  paymentMethod: CheckoutPaymentMethod;
  giftCardType: GiftCardType;
};

export interface PaymentsCapabilities {
  affirm: {
    active: boolean;
    inactiveReason: Stripe.Capability.Requirements.DisabledReason | null;
  };
  afterpay: {
    active: boolean;
    inactiveReason: Stripe.Capability.Requirements.DisabledReason | null;
  };
}

export type GiftCardType = 'internal' | 'external';

export type GiftCardTypeParam = {
  value: GiftCardType;
  set: (value: GiftCardType) => void;
};

export type CheckoutCustomer = CheckoutFormValues['customer'];

export type LineItem = {
  amount: number;
  label: string;
  isTotal?: boolean;
};
